import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';
import { useCRMData } from '../../context/CRMDataContext';

const PROJECT_TYPES = ['Salg', 'Møtebooking', 'Kundeservice', 'Rekruttering', 'Support', 'Onboarding', 'Annet'];
const PROJECT_ROLES = ['Selgere', 'Møtebookere', 'Kundeserviceagenter', 'Supportagenter', 'Rekrutterere', 'Prosjektleder', 'Annet'];
const PRODUCTS_STORAGE_KEY = 'saleswork_demo_products';

const demoResources = [
  { user_id: 'demo-user-1', name: 'Anna Berg', role: 'Selger' },
  { user_id: 'demo-user-2', name: 'Marius Holm', role: 'Møtebooker' },
  { user_id: 'demo-user-3', name: 'Sara Nilsen', role: 'Kundeserviceagent' },
  { user_id: 'demo-user-4', name: 'Jonas Vik', role: 'Supportagent' },
  { user_id: 'demo-user-5', name: 'Emilie Lund', role: 'Prosjektleder' }
];

const productTypeLabels = {
  product: 'Produkt',
  service: 'Tjeneste',
  subscription: 'Abonnement',
  hourly: 'Timebasert',
  commission: 'Provisjon',
  performance: 'Resultatbasert',
  bundle: 'Pakke',
  other: 'Annet'
};

const categoryLabels = {
  standard: 'Standard',
  sales: 'Salg',
  'meeting-booking': 'Møtebooking',
  'customer-service': 'Kundeservice',
  recruitment: 'Rekruttering',
  support: 'Support',
  consulting: 'Konsulenttjeneste',
  saas: 'Programvare/SaaS',
  course: 'Kurs',
  other: 'Annet'
};

const pricingModelLabels = {
  fixed: 'Fastpris',
  hourly: 'Per time',
  monthly: 'Per måned',
  'per-user': 'Per bruker',
  'per-lead': 'Per lead',
  'per-meeting': 'Per møte',
  'per-ticket': 'Per sak/ticket',
  'commission-percent': 'Provisjon %',
  'value-percent': 'Prosent av verdi',
  combination: 'Kombinasjon',
  custom: 'Etter avtale'
};

const unitLabels = {
  stk: 'stk',
  hour: 'time',
  month: 'måned',
  user: 'bruker',
  lead: 'lead',
  meeting: 'møte',
  ticket: 'sak/ticket',
  project: 'prosjekt',
  candidate: 'kandidat',
  agreement: 'avtale',
  other: 'annet'
};

const getLocalStorageArray = key => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatNok = value => `${Number(value || 0).toLocaleString()} NOK`;

const getProductId = product => product.product_id || product.id;

const getCompanyId = company => company?.id || company?.company_id || '';

const getCompanyName = company => (
  company?.name || company?.companyName || company?.title || 'Ukjent selskap'
);

const getProductPriceDisplay = product => {
  const pricingModel = product.pricingModel || 'fixed';

  if (pricingModel === 'custom') return 'Etter avtale';

  if (pricingModel === 'commission-percent' || pricingModel === 'value-percent') {
    return `${Number(product.percentageRate || 0)} %`;
  }

  if (pricingModel === 'combination') {
    return `${formatNok(product.fixedPrice)} + ${Number(product.percentageRate || 0)} %`;
  }

  const unit = unitLabels[product.unit];
  return `${formatNok(product.price)}${unit && product.unit !== 'stk' ? ` / ${unit}` : ''}`;
};

const getNokEstimate = product => {
  const pricingModel = product.pricingModel || 'fixed';

  if (['fixed', 'hourly', 'monthly', 'per-user', 'per-lead', 'per-meeting', 'per-ticket'].includes(pricingModel)) {
    return Number(product.projectPrice ?? product.price) || 0;
  }

  if (pricingModel === 'combination') {
    return Number(product.projectFixedPrice ?? product.fixedPrice) || 0;
  }

  return 0;
};

const isPercentagePricing = product => (
  ['commission-percent', 'value-percent', 'combination'].includes(product.pricingModel)
);

const createDefaultForm = () => ({
  name: '',
  description: '',
  projectType: 'Salg',
  projectFor: 'Internt prosjekt',
  status: 'Planlagt',
  start_date: '',
  end_date: '',
  budget: '',
  budgetAmount: '',
  target: '',
  targetValue: '',
  targetUnit: 'kroner',
  targetPeriod: 'Totalt for prosjektet',
  pricingModel: 'Ikke avklart',
  agreedPrice: '',
  customerName: '',
  organizationNumber: '',
  website: '',
  client_company_id: null,
  client_contacts: [],
  selectedTeamMembers: [],
  selectedProductIds: [],
  selectedProducts: [],
  estimatedProjectValue: 0,
  hasPercentagePricing: false,
  organization: {
    department: '',
    sales_manager: '',
    responsibleLeadId: '',
    responsibleLeadName: '',
    teamAssignmentMode: 'Velges senere',
    expectedTeamSize: '',
    selectedTeamMembers: [],
    projectRoles: [],
    teams: [{ sellers: [] }]
  }
});

const ProjectModal = ({ isOpen, project, onClose, onSave }) => {
  const { users } = useCRM();
  const { addProject, updateProject, companies } = useCRMData();
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState(createDefaultForm);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [saveError, setSaveError] = useState('');

  void onSave;

  const resourceUsers = Array.isArray(users) && users.length > 0 ? users : demoResources;
  const availableCompanies = Array.isArray(companies) ? companies : [];

  useEffect(() => {
    if (!isOpen) return;

    setSaveError('');

    const storedProducts = getLocalStorageArray(PRODUCTS_STORAGE_KEY);
    const activeProducts = storedProducts.filter(product => product.status !== 'inactive');
    setAvailableProducts(activeProducts);

    if (project) {
      const defaults = createDefaultForm();
      const selectedTeamMembers = project.selectedTeamMembers || project.organization?.selectedTeamMembers || [];
      const selectedProducts = Array.isArray(project.selectedProducts) ? project.selectedProducts : [];
      const selectedProductIds = Array.isArray(project.selectedProductIds)
        ? project.selectedProductIds
        : selectedProducts.map(productItem => productItem.productId || productItem.id).filter(Boolean);

      setFormData({
        ...defaults,
        ...project,
        projectType: project.projectType || defaults.projectType,
        projectFor: project.projectFor || defaults.projectFor,
        budget: project.budget ?? project.budgetAmount ?? '',
        budgetAmount: project.budgetAmount ?? project.budget ?? '',
        target: project.target ?? project.targetValue ?? '',
        targetValue: project.targetValue ?? project.target ?? '',
        client_company_id: project.client_company_id || project.clientCompanyId || null,
        client_contacts: project.client_contacts || project.contacts || [],
        selectedTeamMembers,
        selectedProducts,
        selectedProductIds,
        estimatedProjectValue: Number(project.estimatedProjectValue || 0),
        hasPercentagePricing: Boolean(project.hasPercentagePricing),
        organization: {
          ...defaults.organization,
          ...(project.organization || {}),
          sales_manager: project.organization?.sales_manager || project.responsibleLeadName || '',
          responsibleLeadId: project.responsibleLeadId || project.organization?.responsibleLeadId || '',
          responsibleLeadName: project.responsibleLeadName || project.organization?.responsibleLeadName || project.organization?.sales_manager || '',
          teamAssignmentMode: project.teamAssignmentMode || project.organization?.teamAssignmentMode || defaults.organization.teamAssignmentMode,
          expectedTeamSize: project.expectedTeamSize ?? project.organization?.expectedTeamSize ?? '',
          selectedTeamMembers,
          projectRoles: project.projectRoles || project.organization?.projectRoles || [],
          teams: project.organization?.teams?.length > 0 ? project.organization.teams : [{ sellers: [] }]
        }
      });
    } else {
      setFormData(createDefaultForm());
    }

    setProductSearch('');
  }, [project, isOpen]);

  const updateSelectedProducts = nextProducts => {
    const selectedProductIds = nextProducts.map(product => product.productId || product.id).filter(Boolean);
    const estimatedProjectValue = nextProducts.reduce(
      (total, product) => total + getNokEstimate(product),
      0
    );
    const hasPercentagePricing = nextProducts.some(isPercentagePricing);

    setFormData(prev => ({
      ...prev,
      selectedProducts: nextProducts,
      selectedProductIds,
      estimatedProjectValue,
      hasPercentagePricing
    }));
  };

  const toggleProduct = product => {
    const productId = getProductId(product);
    const selectedProducts = formData.selectedProducts || [];
    const isSelected = selectedProducts.some(item => (item.productId || item.id) === productId);

    if (isSelected) {
      updateSelectedProducts(selectedProducts.filter(item => (item.productId || item.id) !== productId));
      return;
    }

    updateSelectedProducts([
      ...selectedProducts,
      {
        productId,
        name: product.name || '',
        description: product.description || '',
        productType: product.productType || 'product',
        category: product.category || 'standard',
        pricingModel: product.pricingModel || 'fixed',
        unit: product.unit || 'stk',
        price: Number(product.price || 0),
        fixedPrice: Number(product.fixedPrice || 0),
        percentageRate: Number(product.percentageRate || 0),
        vatRate: Number(product.vatRate ?? product.vat ?? product.tax ?? 25),
        vat: Number(product.vat ?? product.vatRate ?? product.tax ?? 25),
        projectPrice: '',
        projectFixedPrice: '',
        projectPercentageRate: ''
      }
    ]);
  };

  const updateProductOverride = (productId, field, value) => {
    const nextProducts = (formData.selectedProducts || []).map(product => (
      product.productId === productId ? { ...product, [field]: value } : product
    ));
    updateSelectedProducts(nextProducts);
  };

  const filteredProducts = availableProducts.filter(product => {
    const search = productSearch.trim().toLowerCase();
    if (!search) return true;

    return [
      product.name,
      product.description,
      product.category,
      product.productType,
      product.pricingModel
    ].some(value => String(value || '').toLowerCase().includes(search));
  });

  const handleSubmit = async () => {
    setSaveError('');

    const responsibleLeadName = formData.organization.responsibleLeadName || formData.organization.sales_manager;
    const teamAssignmentMode = formData.organization.teamAssignmentMode;
    const selectedTeamMembers = teamAssignmentMode === 'Legg til personer nå'
      ? formData.selectedTeamMembers || []
      : [];
    const expectedTeamSize = teamAssignmentMode === 'Bruk forventet antall'
      ? formData.organization.expectedTeamSize || null
      : null;
    const organization = {
      ...formData.organization,
      sales_manager: responsibleLeadName,
      teamAssignmentMode,
      selectedTeamMembers,
      expectedTeamSize
    };
    const now = new Date().toISOString();
    const isInternal = formData.projectFor === 'Internt prosjekt';

    const projectData = {
      ...formData,
      selectedProductIds: formData.selectedProductIds || [],
      selectedProducts: formData.selectedProducts || [],
      estimatedProjectValue: Number(formData.estimatedProjectValue || 0),
      hasPercentagePricing: Boolean(formData.hasPercentagePricing),
      updatedAt: now,
      updated_at: now,
      selectedTeamMembers,
      budget: formData.budgetAmount || formData.budget,
      budgetAmount: formData.budgetAmount || formData.budget,
      target: formData.targetValue || formData.target,
      targetValue: formData.targetValue || formData.target,
      contacts: formData.client_contacts,
      responsibleLeadId: formData.organization.responsibleLeadId,
      responsibleLeadName,
      teamAssignmentMode,
      expectedTeamSize,
      projectRoles: formData.organization.projectRoles,
      organization
    };

    const projectPayload = {
      name: projectData.name,
      description: projectData.description,
      project_type: projectData.projectType,
      project_for: projectData.projectFor,
      status: projectData.status,
      start_date: projectData.start_date || null,
      end_date: projectData.end_date || null,
      budget: projectData.budget,
      target_value: projectData.targetValue,
      target_unit: projectData.targetUnit,
      target_period: projectData.targetPeriod,
      pricing_model: projectData.pricingModel,
      agreed_price: projectData.agreedPrice === '' ? null : projectData.agreedPrice,
      is_internal: isInternal,
      client_company_id: isInternal ? null : (projectData.client_company_id || null)
    };

    try {
      const result = project?.id
        ? await updateProject(project.id, projectPayload)
        : await addProject(projectPayload);

      if (!result?.success) {
        const errorMessage = result?.error?.message || result?.error || 'Kunne ikke lagre prosjektet.';
        console.error('[ProjectModal] Prosjektet ble ikke lagret.', result);
        setSaveError(errorMessage);
        return;
      }

      onClose();
    } catch (error) {
      console.error('[ProjectModal] Uventet feil ved lagring av prosjekt.', error);
      setSaveError(error?.message || 'Kunne ikke lagre prosjektet.');
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateProjectFor = value => {
    setFormData(prev => ({
      ...prev,
      projectFor: value,
      client_company_id: value === 'Internt prosjekt' ? null : prev.client_company_id
    }));
  };

  const updateOrganizationField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      organization: { ...prev.organization, [field]: value }
    }));
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      client_contacts: [...prev.client_contacts, { name: '', phone: '', email: '', role: '' }]
    }));
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.client_contacts];
    newContacts[index][field] = value;
    setFormData(prev => ({ ...prev, client_contacts: newContacts }));
  };

  const toggleRole = role => {
    const currentRoles = formData.organization.projectRoles || [];
    updateOrganizationField(
      'projectRoles',
      currentRoles.includes(role)
        ? currentRoles.filter(item => item !== role)
        : [...currentRoles, role]
    );
  };

  const toggleSeller = userName => {
    setFormData(prev => {
      const currentTeams = prev.organization.teams || [{}];
      const firstTeam = currentTeams[0] || {};
      const currentSellers = firstTeam.sellers || [];
      const newSellers = currentSellers.includes(userName)
        ? currentSellers.filter(name => name !== userName)
        : [...currentSellers, userName];
      const newTeams = [...currentTeams];
      newTeams[0] = { ...firstTeam, sellers: newSellers };

      return { ...prev, organization: { ...prev.organization, teams: newTeams } };
    });
  };

  const toggleSelectedTeamMember = user => {
    setFormData(prev => {
      const currentMembers = prev.selectedTeamMembers || [];
      const isSelected = currentMembers.some(member => member.id === user.user_id);
      const selectedTeamMembers = isSelected
        ? currentMembers.filter(member => member.id !== user.user_id)
        : [...currentMembers, { id: user.user_id, name: user.name, role: user.role }];

      return {
        ...prev,
        selectedTeamMembers,
        organization: { ...prev.organization, selectedTeamMembers }
      };
    });
  };

  if (!isOpen) return null;

  const currentSellers = formData.organization.teams?.[0]?.sellers || [];
  const selectedTeamMembers = formData.selectedTeamMembers || [];
  const selectedProducts = formData.selectedProducts || [];

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-slate-50">
            <h2 className="text-xl font-bold text-slate-800">
              {project ? 'Rediger Prosjekt' : 'Nytt Prosjekt'}
            </h2>
            <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-200 rounded-full">
              <SafeIcon icon="✕" />
            </button>
          </div>

          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveStep(1)}
              className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeStep === 1 ? 'border-primary-600 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500'}`}
            >
              1. Generelt & Økonomi
            </button>
            <button
              onClick={() => setActiveStep(2)}
              className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeStep === 2 ? 'border-primary-600 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500'}`}
            >
              2. Kunde & Kontakt
            </button>
            <button
              onClick={() => setActiveStep(3)}
              className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeStep === 3 ? 'border-primary-600 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500'}`}
            >
              3. Organisering
            </button>
            <button
              onClick={() => setActiveStep(4)}
              className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeStep === 4 ? 'border-primary-600 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500'}`}
            >
              4. Produkter & tjenester
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-8">
            {activeStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prosjektnavn</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      value={formData.name}
                      onChange={e => updateField('name', e.target.value)}
                      placeholder="F.eks. Nordlys Teknikk – Kundeservice Q4"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Beskrivelse</label>
                    <textarea
                      rows={3}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      value={formData.description}
                      onChange={e => updateField('description', e.target.value)}
                      placeholder="Beskriv prosjektet, målgruppe, leveranse og hovedmål..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prosjekttype</label>
                    <select className="w-full px-4 py-2 border border-slate-300 rounded-lg" value={formData.projectType} onChange={e => updateField('projectType', e.target.value)}>
                      {PROJECT_TYPES.map(type => <option key={type}>{type}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prosjekt for</label>
                    <select className="w-full px-4 py-2 border border-slate-300 rounded-lg" value={formData.projectFor} onChange={e => updateProjectFor(e.target.value)}>
                      <option>Internt prosjekt</option>
                      <option>Kundeprosjekt</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select className="w-full px-4 py-2 border border-slate-300 rounded-lg" value={formData.status} onChange={e => updateField('status', e.target.value)}>
                      <option value="Planlagt">Planlagt</option>
                      <option value="Aktivt">Aktivt</option>
                      <option value="Pauset">Pauset</option>
                      <option value="Fullført">Fullført</option>
                    </select>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Tidslinje & Mål</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Startdato</label>
                      <div className="relative">
                        <SafeIcon icon="📅" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input type="date" className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm" value={formData.start_date} onChange={e => updateField('start_date', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Sluttdato (Frist)</label>
                      <div className="relative">
                        <SafeIcon icon="🏁" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input type="date" className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm" value={formData.end_date} onChange={e => updateField('end_date', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Budsjett / kostnadsramme</label>
                      <span className="block text-[11px] font-normal text-slate-400 mb-1">Totalt budsjett eller kostnadsramme for prosjektet.</span>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">kr</span>
                        <input type="number" className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg text-sm font-bold" value={formData.budgetAmount} onChange={e => updateField('budgetAmount', e.target.value)} placeholder="0" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Målverdi / leveransemål</label>
                      <span className="block text-[11px] font-normal text-slate-400 mb-1">Kan være omsetning, bookede møter, leads, løste saker, kandidater eller annen prosjektverdi.</span>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500 text-xs font-bold">kr</span>
                        <input type="number" className="w-full pl-8 pr-4 py-2 border border-emerald-200 rounded-lg text-sm font-bold text-emerald-700 bg-emerald-50 focus:ring-emerald-500 focus:border-emerald-500" value={formData.targetValue} onChange={e => updateField('targetValue', e.target.value)} placeholder="0" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Måleenhet</label>
                      <select className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm" value={formData.targetUnit} onChange={e => updateField('targetUnit', e.target.value)}>
                        {['kroner', 'møter', 'leads', 'saker/tickets', 'kandidater', 'timer', 'annet'].map(unit => <option key={unit}>{unit}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Målperiode</label>
                      <select className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm" value={formData.targetPeriod} onChange={e => updateField('targetPeriod', e.target.value)}>
                        {['Totalt for prosjektet', 'Per måned', 'Per uke', 'Per agent/selger', 'Per team'].map(period => <option key={period}>{period}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Pris-/avtalemodell</label>
                      <select className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm" value={formData.pricingModel} onChange={e => updateField('pricingModel', e.target.value)}>
                        {['Fastpris', 'Timebasert', 'Provisjon', 'Pris per møte', 'Pris per lead', 'Pris per sak/ticket', 'Retainer/månedsavtale', 'Kombinasjon', 'Ikke avklart'].map(model => <option key={model}>{model}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Avtalt pris</label>
                      <input type="number" className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm" value={formData.agreedPrice} onChange={e => updateField('agreedPrice', e.target.value)} placeholder="kr" />
                      <span className="block text-[11px] font-normal text-slate-400 mt-1">Brukes hvis prosjektet har fastpris, retainer eller annen avtalt pris.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-6">
                <h3 className="font-bold text-slate-900">Kunde / oppdragsgiver</h3>
                {formData.projectFor === 'Kundeprosjekt' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Oppdragsgiver</label>
                    <select
                      className="w-full px-3 py-2 border rounded"
                      value={formData.client_company_id || ''}
                      onChange={e => updateField('client_company_id', e.target.value || null)}
                    >
                      <option value="">-- Velg oppdragsgiver --</option>
                      {availableCompanies.map(company => {
                        const companyId = getCompanyId(company);
                        return (
                          <option key={companyId} value={companyId}>
                            {getCompanyName(company)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="Kundenavn / avdeling" className="px-3 py-2 border rounded" value={formData.customerName} onChange={e => updateField('customerName', e.target.value)} />
                  <input placeholder="Organisasjonsnummer hvis relevant" className="px-3 py-2 border rounded" value={formData.organizationNumber} onChange={e => updateField('organizationNumber', e.target.value)} />
                  <input placeholder="Nettside" className="px-3 py-2 border rounded md:col-span-2" value={formData.website} onChange={e => updateField('website', e.target.value)} />
                </div>
                <div className="flex justify-between items-center">
                  <h3 className="font-medium text-slate-900">Kontaktpersoner</h3>
                  <button onClick={addContact} className="text-sm text-primary-600 hover:underline">+ Legg til kontakt</button>
                </div>
                {formData.client_contacts.map((contact, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="Primær kontaktperson" className="px-3 py-2 border rounded" value={contact.name} onChange={e => updateContact(idx, 'name', e.target.value)} />
                      <input placeholder="Rolle / Tittel" className="px-3 py-2 border rounded" value={contact.role} onChange={e => updateContact(idx, 'role', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="Telefon" className="px-3 py-2 border rounded" value={contact.phone} onChange={e => updateContact(idx, 'phone', e.target.value)} />
                      <input placeholder="E-post" className="px-3 py-2 border rounded" value={contact.email} onChange={e => updateContact(idx, 'email', e.target.value)} />
                    </div>
                  </div>
                ))}
                {formData.client_contacts.length === 0 && <p className="text-center text-slate-400 italic">Ingen kontakter lagt til.</p>}
              </div>
            )}

            {activeStep === 3 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Avdeling</label>
                    <input className="w-full px-4 py-2 border border-slate-300 rounded-lg" value={formData.organization.department} onChange={e => updateOrganizationField('department', e.target.value)} placeholder="F.eks. Salg Nord" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ansvarlig leder</label>
                    <p className="text-xs text-slate-500 mb-1">Salgssjef, teamleder eller prosjektleder.</p>
                    <select className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-white" value={formData.organization.responsibleLeadId} onChange={e => {
                      const selectedUser = users.find(user => user.user_id === e.target.value);
                      updateOrganizationField('responsibleLeadId', e.target.value);
                      updateOrganizationField('responsibleLeadName', selectedUser?.name || '');
                    }}>
                      <option value="">-- Velg ansvarlig leder --</option>
                      {users.map(user => <option key={user.user_id} value={user.user_id}>{user.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><SafeIcon icon="👥" className="text-primary-600" /> Team / agenter / ressurser</h3>
                  <div className="flex flex-wrap gap-4 mb-4">
                    {['Legg til personer nå', 'Velges senere', 'Bruk forventet antall'].map(mode => (
                      <label key={mode} className="flex items-center gap-2 text-sm">
                        <input type="radio" name="teamAssignmentMode" value={mode} checked={formData.organization.teamAssignmentMode === mode} onChange={e => updateOrganizationField('teamAssignmentMode', e.target.value)} />
                        {mode}
                      </label>
                    ))}
                  </div>

                  {formData.organization.teamAssignmentMode === 'Legg til personer nå' && (
                    <>
                      <p className="text-sm font-medium text-slate-700 mb-3">Tilgjengelige ressurser</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[200px] overflow-y-auto">
                        {resourceUsers.map(user => {
                          const isSelected = selectedTeamMembers.some(member => member.id === user.user_id);
                          return (
                            <label key={user.user_id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'bg-primary-50 border-primary-200 ring-1 ring-primary-500' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                              <input type="checkbox" className="hidden" checked={isSelected} onChange={() => toggleSelectedTeamMember(user)} />
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${isSelected ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{user.name.substring(0, 2).toUpperCase()}</div>
                              <div><p className={`text-sm font-bold ${isSelected ? 'text-primary-900' : 'text-slate-700'}`}>{user.name}</p><p className="text-xs text-slate-500">{user.role}</p></div>
                              {isSelected && <SafeIcon icon="✓" className="ml-auto text-primary-600" />}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-sm text-primary-700 mt-2">{selectedTeamMembers.length} personer valgt</p>
                    </>
                  )}

                  {formData.organization.teamAssignmentMode === 'Velges senere' && <p className="text-sm text-slate-500 mb-3">Teamet kan settes senere.</p>}
                  {formData.organization.teamAssignmentMode === 'Bruk forventet antall' && (
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Forventet antall personer
                      <input type="number" className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg" value={formData.organization.expectedTeamSize} onChange={e => updateOrganizationField('expectedTeamSize', e.target.value)} placeholder="F.eks. 5" />
                      <span className="text-xs font-normal text-slate-500">Brukes hvis teamet ikke er satt ennå.</span>
                    </label>
                  )}
                  <p className="text-xs text-slate-500 mt-2 italic">Valgte personer får tilgang til prosjektet i sin portefølje. Hvis teamet ikke er klart, bruk forventet antall.</p>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-3">Roller i prosjektet</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {PROJECT_ROLES.map(role => (
                      <label key={role} className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={formData.organization.projectRoles.includes(role)} onChange={() => toggleRole(role)} />
                        {role}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-slate-900">Produkter & tjenester</h3>
                  <p className="text-sm text-slate-500 mt-1">Velg hvilke produkter, tjenester eller prismodeller som gjelder for dette prosjektet.</p>
                </div>

                {availableProducts.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                    <p className="text-sm font-medium text-slate-600">Ingen produkter/tjenester er lagt inn ennå. Gå til Produktkatalog for å opprette produkter.</p>
                  </div>
                ) : (
                  <>
                    <input
                      type="search"
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      placeholder="Søk i produkter/tjenester..."
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                    />

                    <div className="space-y-3">
                      {filteredProducts.map(product => {
                        const productId = getProductId(product);
                        const selected = selectedProducts.find(item => item.productId === productId);
                        const isSelected = Boolean(selected);

                        return (
                          <div key={productId} className={`rounded-xl border p-4 transition-colors ${isSelected ? 'border-primary-300 bg-primary-50/50' : 'border-slate-200 bg-white'}`}>
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input type="checkbox" checked={isSelected} onChange={() => toggleProduct(product)} className="mt-1" />
                              <span className="min-w-0">
                                <span className="block font-bold text-slate-900">{product.name}</span>
                                <span className="block text-xs text-slate-500 mt-1">
                                  {productTypeLabels[product.productType] || product.productType || 'Produkt'} · {categoryLabels[product.category] || product.category || 'Standard'} · {pricingModelLabels[product.pricingModel] || 'Fastpris'}
                                </span>
                                <span className="block text-sm font-bold text-slate-700 mt-1">{getProductPriceDisplay(product)}</span>
                              </span>
                            </label>

                            {isSelected && (
                              <div className="mt-4 ml-7 grid grid-cols-1 md:grid-cols-3 gap-3">
                                {['commission-percent', 'value-percent'].includes(selected.pricingModel) && (
                                  <label className="text-xs font-bold text-slate-600">
                                    Prosjektprosent
                                    <input type="number" min="0" max="100" value={selected.projectPercentageRate} onChange={e => updateProductOverride(productId, 'projectPercentageRate', e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg font-normal" placeholder={String(selected.percentageRate || 0)} />
                                  </label>
                                )}
                                {selected.pricingModel === 'combination' && (
                                  <>
                                    <label className="text-xs font-bold text-slate-600">
                                      Prosjekt fast del
                                      <input type="number" value={selected.projectFixedPrice} onChange={e => updateProductOverride(productId, 'projectFixedPrice', e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg font-normal" placeholder={String(selected.fixedPrice || 0)} />
                                    </label>
                                    <label className="text-xs font-bold text-slate-600">
                                      Prosjekt prosentdel
                                      <input type="number" min="0" max="100" value={selected.projectPercentageRate} onChange={e => updateProductOverride(productId, 'projectPercentageRate', e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg font-normal" placeholder={String(selected.percentageRate || 0)} />
                                    </label>
                                  </>
                                )}
                                {!isPercentagePricing(selected) && selected.pricingModel !== 'combination' && selected.pricingModel !== 'custom' && (
                                  <label className="text-xs font-bold text-slate-600">
                                    Prosjektpris / avtalt pris
                                    <input type="number" value={selected.projectPrice} onChange={e => updateProductOverride(productId, 'projectPrice', e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg font-normal" placeholder={String(selected.price || 0)} />
                                  </label>
                                )}
                                {selected.pricingModel === 'custom' && (
                                  <p className="text-xs text-slate-500 md:col-span-3">Pris avtales manuelt i tilbud eller kontrakt.</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {filteredProducts.length === 0 && <p className="text-sm text-slate-500">Ingen produkter matcher søket.</p>}

                    <div className="border-t border-slate-200 pt-4 space-y-1">
                      <p className="text-sm font-bold text-slate-700">Valgt: {selectedProducts.length} produkter/tjenester</p>
                      <p className="text-sm font-bold text-slate-700">Estimert prosjektverdi: {formatNok(formData.estimatedProjectValue)}</p>
                      {formData.hasPercentagePricing && <p className="text-xs text-slate-500">Prosentbaserte produkter beregnes i tilbud/kontrakt.</p>}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between px-6 py-4 border-t border-slate-100 bg-slate-50">
            {activeStep > 1 ? (
              <button onClick={() => setActiveStep(prev => prev - 1)} className="text-slate-600 font-medium hover:text-slate-900">Forrige</button>
            ) : <div />}

            {activeStep < 4 ? (
              <button onClick={() => setActiveStep(prev => prev + 1)} className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700">Neste</button>
            ) : (
              <button onClick={handleSubmit} className="px-6 py-2 bg-primary-600 text-white rounded-lg shadow-lg hover:bg-primary-700">
                <SafeIcon icon="💾" className="inline mr-2" /> Lagre Prosjekt
              </button>
            )}
          </div>
          {saveError && <p className="px-6 pb-4 text-sm text-red-600">{saveError}</p>}
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default ProjectModal;